// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import {
  FulfillmentOrder,
  UpdateFulfillmentOrderRequest,
  fulfillmentOrderSchema,
} from '@/fulfillment/api/types';
import { serializeDateForAPI } from '@/utilities/date-time';
import { z } from 'zod';

/**
 * Schema for validating the response from the update fulfillment order API endpoint.
 */
const updateFulfillmentOrderResponseSchema = z.object({
  data: fulfillmentOrderSchema,
  metadata: responseMetadataSchema,
});

/**
 * Updates an existing fulfillment order with the specified details.
 *
 * @param fulfillmentKey - The key of the fulfillment order to update
 * @param request - The fulfillment order details to update
 * @returns The updated fulfillment order
 */
export async function updateFulfillmentOrder(
  fulfillmentKey: string,
  request: UpdateFulfillmentOrderRequest
): Promise<FulfillmentOrder> {
  const url = `/v1/fulfillment/${fulfillmentKey}`;
  const apiRequest = {
    ...request,
    promiseShipDate: serializeDateForAPI(request.promiseShipDate),
    promiseDeliveryDate: serializeDateForAPI(request.promiseDeliveryDate),
  };
  const response = await api.patch(url, apiRequest);

  const { data } = await parseResponse(
    updateFulfillmentOrderResponseSchema,
    response.data,
    {
      schemaName: 'UpdateFulfillmentOrder',
      endpoint: url,
    }
  );
  return data;
}
